import { useEffect, useRef, useState } from "react"

import Changelog from "./components/Changelog"
import ConnectSection from "./components/ConnectSection"
import Sidebar from "./components/Sidebar"
import ToolsTable from "./components/ToolsTable"
import { EndpointBox, Notice } from "./components/ui"

import "./mcp-doc.css"

const SECTION_IDS = [
  "introduction",
  "what-it-does",
  "tools",
  "endpoint",
  "connect",
  "other-ways",
  "how-to-ask",
  "limitations",
  "security",
  "security-tips",
  "changelog",
  "changelog-1-0"
]

const McpDocPage = () => {
  const [activeSection, setActiveSection] = useState("introduction")
  const mainRef = useRef(null)

  useEffect(() => {
    const container = mainRef.current
    if (!container) return

    const updateActive = () => {
      const containerRect = container.getBoundingClientRect()
      const targetY = containerRect.top + containerRect.height * 0.25

      let active = SECTION_IDS[0]
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id)
        if (el && el.getBoundingClientRect().top <= targetY) {
          active = id
        }
      }
      setActiveSection(active)
    }

    container.addEventListener("scroll", updateActive, { passive: true })
    updateActive()
    return () => container.removeEventListener("scroll", updateActive)
  }, [])

  return (
    <div className="mcp-doc-page">
      <Sidebar activeSection={activeSection} />

      <main className="mcp-doc-main" ref={mainRef}>
        <div className="mcp-doc-inner">
          {/* ── Page header ─────────────────────────────────────── */}
          <div className="mcp-doc-page-tags">
            <span className="mcp-doc-page-tag">MCP Integration</span>
            <span className="mcp-doc-page-tag">Read + write</span>
          </div>
          <h1 className="mcp-doc-page-title">Chargeback MCP</h1>
          <p className="mcp-doc-page-intro">
            Connect Claude, ChatGPT, Cursor, and other AI assistants to your Chargeback account.
            Once connected, you can ask questions about your alerts and business accounts in plain
            language. The assistant fetches live data from Chargeback.io on demand, with no
            copy-pasting or manual exports.
          </p>

          {/* ── Introduction ────────────────────────────────────── */}
          <section className="mcp-doc-section" id="introduction">
            <h2 className="mcp-doc-section-title">Introduction</h2>
            <div className="mcp-doc-prose">
              <p>
                The Chargeback MCP server implements the <strong>Model Context Protocol</strong>, an
                open standard that lets AI assistants connect to external data sources. When you
                connect your AI client to the Chargeback MCP, it can fetch your real account data,
                including alerts, business accounts, and enrollment status. It can then reason over
                that data alongside your questions.
              </p>
              <p>
                You log in once through a browser window and choose what the assistant is allowed to
                see. You can revoke access at any time in your AI client's settings. Authentication
                uses OAuth 2.1.
              </p>
            </div>
          </section>

          {/* ── What it can do ───────────────────────────────────── */}
          <section className="mcp-doc-section" id="what-it-does">
            <h2 className="mcp-doc-section-title">What it can do</h2>
            <p className="mcp-doc-section-intro">
              The Chargeback MCP can read your data and change an alert's status. Reading and
              changing status are equal capabilities, each granted through its own permission when
              you connect. Changing an alert's status works the same as on the platform and follows
              the same rules and restrictions. The MCP cannot create, delete, or otherwise modify
              anything else.
            </p>
            <div className="mcp-doc-bounds-row">
              <div className="mcp-doc-bounds-col can">
                <div className="mcp-doc-bounds-col-title">WHAT IT CAN DO</div>
                <ul className="mcp-doc-bounds-list">
                  <li>
                    <span className="mcp-doc-bounds-marker">✓</span> Read alerts and their details
                  </li>
                  <li>
                    <span className="mcp-doc-bounds-marker">✓</span> List and filter business
                    accounts
                  </li>
                  <li>
                    <span className="mcp-doc-bounds-marker">✓</span> Check account enrollment status
                  </li>
                  <li>
                    <span className="mcp-doc-bounds-marker">✓</span> Answer questions about your
                    data
                  </li>
                  <li>
                    <span className="mcp-doc-bounds-marker">✓</span> Change an alert's status (with
                    the write permission)
                  </li>
                </ul>
              </div>
              <div className="mcp-doc-bounds-col cannot">
                <div className="mcp-doc-bounds-col-title">WHAT IT CAN'T DO</div>
                <ul className="mcp-doc-bounds-list">
                  <li>
                    <span className="mcp-doc-bounds-marker">✕</span> Create or delete alerts
                  </li>
                  <li>
                    <span className="mcp-doc-bounds-marker">✕</span> Change anything on an alert
                    other than its status
                  </li>
                  <li>
                    <span className="mcp-doc-bounds-marker">✕</span> Change account settings
                  </li>
                  <li>
                    <span className="mcp-doc-bounds-marker">✕</span> Access another user's data
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* ── Tools ────────────────────────────────────────────── */}
          <section className="mcp-doc-section" id="tools">
            <h2 className="mcp-doc-section-title">Tools</h2>
            <p className="mcp-doc-section-intro">
              The MCP exposes five tools: four read-only tools, plus one write tool for changing an
              alert's status. Each tool enforces per-user data isolation, so you can only retrieve
              data your Chargeback.io account has access to.
            </p>
            <ToolsTable />
            <Notice type="info">
              All tools support a <code>verbose</code> mode that returns the full field set. By
              default, low-signal fields are omitted and money is formatted as dollars. Customer
              personal data is always masked, in every mode.
            </Notice>
          </section>

          {/* ── Endpoint ─────────────────────────────────────────── */}
          <section className="mcp-doc-section" id="endpoint">
            <h2 className="mcp-doc-section-title">MCP endpoint</h2>
            <p className="mcp-doc-section-intro">
              This is the URL you'll enter when setting up any MCP client. You don't need an API
              key. You sign in through your browser, and the client finds the auth flow
              automatically.
            </p>
            <EndpointBox />
          </section>

          {/* ── Connect ──────────────────────────────────────────── */}
          <section className="mcp-doc-section" id="connect">
            <h2 className="mcp-doc-section-title">How to connect</h2>
            <p className="mcp-doc-section-intro">
              Select your AI client to see setup instructions. Menu names may vary as clients update
              their interfaces, but the core flow is always the same: paste the endpoint URL, sign
              in to Chargeback.io, and approve access. When you approve access, you choose which
              permissions to grant: reading alerts, reading business accounts, and changing alert
              statuses. Grant all three for full functionality, or only the ones you want the
              assistant to use.
            </p>
            <ConnectSection />
          </section>

          {/* ── Other ways ───────────────────────────────────────── */}
          <section className="mcp-doc-section" id="other-ways">
            <h2 className="mcp-doc-section-title">Don't see your client here?</h2>
            <div className="mcp-doc-prose">
              <p>
                Most MCP-compatible clients can connect without a dedicated guide. Look for an MCP,
                connectors, or plugins section in your client's settings. Any client that supports
                streamable-HTTP transport and OAuth 2.1 will work with the Chargeback.io endpoint.
                The client handles authentication automatically once it has the URL.
              </p>
            </div>
            <EndpointBox />
            <Notice type="info">
              Connected a client that's not on our list?{" "}
              <a
                href="mailto:support@chargeback.io"
                style={{
                  color: "inherit",
                  textDecoration: "none",
                  borderBottom: "1px solid #93c5fd"
                }}
              >
                Let us know.
              </a>{" "}
              If it works well, we'll add a guide for it.
            </Notice>
          </section>

          {/* ── How to ask ───────────────────────────────────────── */}
          <section className="mcp-doc-section" id="how-to-ask">
            <h2 className="mcp-doc-section-title">How to ask for data</h2>
            <p className="mcp-doc-section-intro">
              The way you phrase a question affects what you get back. Specific questions return
              specific answers.
            </p>

            <div className="mcp-doc-prompt-row">
              <div className="mcp-doc-prompt-col good">
                <div className="mcp-doc-prompt-col-label">Works well</div>
                <div className="mcp-doc-prompt-item">
                  Show me all refunded Ethoca alerts from the last 7 days
                </div>
                <div className="mcp-doc-prompt-item">
                  How many alerts did business account ACME have in June?
                </div>
                <div className="mcp-doc-prompt-item">
                  List business accounts with incomplete enrollment status
                </div>
                <div className="mcp-doc-prompt-item">
                  Get alert CB-00482 and summarise what happened
                </div>
                <div className="mcp-doc-prompt-note">
                  These name a time range, account, status, or ID. The assistant can form a precise
                  query.
                </div>
              </div>
              <div className="mcp-doc-prompt-col bad">
                <div className="mcp-doc-prompt-col-label">Works poorly</div>
                <div className="mcp-doc-prompt-item">Show me everything</div>
                <div className="mcp-doc-prompt-item">What's going on with my chargebacks?</div>
                <div className="mcp-doc-prompt-item">Are things looking good?</div>
                <div className="mcp-doc-prompt-item">What should I do next?</div>
                <div className="mcp-doc-prompt-note">
                  These are too vague. The assistant has to guess what you want. It may return
                  partial data or ask a chain of clarifying questions.
                </div>
              </div>
            </div>

            <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", margin: "24px 0 12px" }}>
              What a real conversation looks like
            </p>

            <div className="mcp-doc-dialogue">
              <div className="mcp-doc-dialogue-header">Reviewing this month's refunded alerts</div>
              <div className="mcp-doc-dialogue-body">
                <div className="mcp-doc-dialogue-turn user">
                  <div className="mcp-doc-dialogue-role">You</div>
                  <div className="mcp-doc-dialogue-text">
                    Show my alerts refunded during this month
                  </div>
                </div>

                <div className="mcp-doc-dialogue-turn ai">
                  <div className="mcp-doc-dialogue-role">AI</div>
                  <div className="mcp-doc-dialogue-text">
                    <p style={{ marginBottom: 10 }}>
                      Here's what I found. Alerts with status <strong>Auto-refunded</strong> during
                      June 2026 (i.e. refunded automatically due to your auto-refund settings):
                    </p>
                    <p style={{ marginBottom: 8, fontWeight: 600, fontSize: 13 }}>Summary</p>
                    <div className="mcp-doc-dialogue-table-scroll">
                      <table className="mcp-doc-dialogue-table">
                        <thead>
                          <tr>
                            <th>Business Account</th>
                            <th># Refunded Alerts</th>
                            <th>Total Refunded</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Nordic Apparel Co</td>
                            <td>24</td>
                            <td>$2,020.00</td>
                          </tr>
                          <tr>
                            <td>Coastal Home Goods</td>
                            <td>16</td>
                            <td>$1,203.00</td>
                          </tr>
                          <tr>
                            <td style={{ fontWeight: 600, color: "#1a1a1a" }}>Total</td>
                            <td style={{ fontWeight: 600, color: "#1a1a1a" }}>40</td>
                            <td style={{ fontWeight: 600, color: "#1a1a1a" }}>$3,223.00</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p style={{ marginBottom: 8 }}>
                      All in USD, via a mix of Ethoca, CDRN, and RDR alert services.
                    </p>
                    <p>
                      A note: every one of these has the same system message:{" "}
                      <em>
                        "Auto-refund is turned off in your business account... please refund
                        manually and set to 'Manually refunded'."
                      </em>{" "}
                      That suggests these are technically flagged as "Auto-refunded" in status, but
                      your actual auto-refund toggle is off for both accounts, so you may want to
                      check whether these transactions were actually refunded on your end or just
                      marked that way.
                    </p>
                    <p style={{ marginTop: 8 }}>
                      Want me to pull the full list of individual alerts (with card last4, amounts,
                      dates) for either account, or check the auto-refund settings on these business
                      accounts?
                    </p>
                  </div>
                </div>

                <div className="mcp-doc-dialogue-turn user">
                  <div className="mcp-doc-dialogue-role">You</div>
                  <div className="mcp-doc-dialogue-text">Retrieve the full list</div>
                </div>

                <div className="mcp-doc-dialogue-turn ai">
                  <div className="mcp-doc-dialogue-role">AI</div>
                  <div className="mcp-doc-dialogue-text">
                    <p style={{ marginBottom: 10 }}>
                      Here's the complete list of June 2026 auto-refunded alerts:
                    </p>
                    <p style={{ marginBottom: 8, fontWeight: 600, fontSize: 13 }}>
                      Nordic Apparel Co (24 alerts, $2,020.00)
                    </p>
                    <div className="mcp-doc-dialogue-table-scroll">
                      <table className="mcp-doc-dialogue-table mcp-doc-dialogue-table-sm">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Amount</th>
                            <th>Service</th>
                            <th>Card</th>
                            <th>Alert ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>06-02</td>
                            <td>$43.00</td>
                            <td>rdr</td>
                            <td>Visa •3195</td>
                            <td>656332886</td>
                          </tr>
                          <tr>
                            <td>06-02</td>
                            <td>$49.00</td>
                            <td>rdr</td>
                            <td>Visa •4953</td>
                            <td>679094884</td>
                          </tr>
                          <tr>
                            <td>06-02</td>
                            <td>$59.00</td>
                            <td>rdr</td>
                            <td>Visa •5037</td>
                            <td>675142767</td>
                          </tr>
                          <tr>
                            <td>06-02</td>
                            <td>$105.00</td>
                            <td>rdr</td>
                            <td>Visa •7473</td>
                            <td>94384207</td>
                          </tr>
                          <tr>
                            <td>06-02</td>
                            <td>$131.00</td>
                            <td>rdr</td>
                            <td>Visa •6871</td>
                            <td>635717614</td>
                          </tr>
                          <tr>
                            <td>06-03</td>
                            <td>$49.00</td>
                            <td>rdr</td>
                            <td>Visa •8422</td>
                            <td>627790148</td>
                          </tr>
                          <tr>
                            <td>06-11</td>
                            <td>$122.00</td>
                            <td>ethoca</td>
                            <td>Visa •4893</td>
                            <td>9K9INTPTY9PI1SZBXZVGFCYPH</td>
                          </tr>
                          <tr>
                            <td>06-22</td>
                            <td>$70.00</td>
                            <td>ethoca</td>
                            <td>Visa •1124</td>
                            <td>932QAJO5UNWS2VZHBSHWMDAGK</td>
                          </tr>
                          <tr>
                            <td className="mcp-doc-dialogue-more" colSpan={5}>
                              + 16 more rows
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p style={{ marginBottom: 8, fontWeight: 600, fontSize: 13 }}>
                      Coastal Home Goods (16 alerts, $1,203.00)
                    </p>
                    <div className="mcp-doc-dialogue-table-scroll">
                      <table className="mcp-doc-dialogue-table mcp-doc-dialogue-table-sm">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Amount</th>
                            <th>Service</th>
                            <th>Card</th>
                            <th>Alert ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>06-03</td>
                            <td>$114.00</td>
                            <td>cdrn</td>
                            <td>Visa •1827</td>
                            <td>774002168</td>
                          </tr>
                          <tr>
                            <td>06-06</td>
                            <td>$30.00</td>
                            <td>rdr</td>
                            <td>Visa •9133</td>
                            <td>614574221</td>
                          </tr>
                          <tr>
                            <td>06-15</td>
                            <td>$82.00</td>
                            <td>ethoca</td>
                            <td>Visa •5629</td>
                            <td>LULVYPPHLSORPSVXQABEP9WT7</td>
                          </tr>
                          <tr>
                            <td>06-16</td>
                            <td>$100.00</td>
                            <td>ethoca</td>
                            <td>Visa •9848</td>
                            <td>46WVK69EL67X10DQ7GGQMO5HC</td>
                          </tr>
                          <tr>
                            <td>06-22</td>
                            <td>$103.00</td>
                            <td>ethoca</td>
                            <td>Visa •6435</td>
                            <td>2MFKXESVKE8HFZVVUME9ZODXM</td>
                          </tr>
                          <tr>
                            <td>06-23</td>
                            <td>$120.00</td>
                            <td>rdr</td>
                            <td>Visa •5798</td>
                            <td>605102076</td>
                          </tr>
                          <tr>
                            <td className="mcp-doc-dialogue-more" colSpan={5}>
                              + 10 more rows
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p style={{ marginBottom: 4, fontWeight: 600, color: "#1a1a1a" }}>
                      Total: 40 alerts, $3,223.00
                    </p>
                    <p>
                      All of these carry the same system note that auto-refund is actually turned{" "}
                      <strong>off</strong> on both accounts. Worth double-checking that these
                      transactions were genuinely refunded, or flip on auto-refund in Business
                      Settings if you want this handled automatically going forward.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Notice type="info">
              Narrowing by filters (status, date, alert product, account) in your first message
              produces faster, more accurate results. The assistant can form a precise API query
              instead of fetching everything and sorting through it.
            </Notice>
          </section>

          {/* ── Limitations ──────────────────────────────────────── */}
          <section className="mcp-doc-section" id="limitations">
            <h2 className="mcp-doc-section-title">Limitations</h2>
            <p className="mcp-doc-section-intro">
              The MCP gives your AI assistant real data, but it doesn't make the assistant perfect
              or unlimited.
            </p>
            <div className="mcp-doc-limit-list">
              <div className="mcp-doc-limit-item">
                <div className="mcp-doc-limit-item-title">AI interpretation can be wrong</div>
                <div className="mcp-doc-limit-item-desc">
                  The assistant fetches real data, but it can summarize, compare, or draw
                  conclusions incorrectly. Numerical totals, date ranges, and status aggregations
                  are the most error-prone. Verify anything decision-critical directly in your
                  Chargeback.io dashboard.
                </div>
              </div>
              <div className="mcp-doc-limit-item">
                <div className="mcp-doc-limit-item-title">
                  Some AI plans restrict or limit MCP functionality
                </div>
                <div className="mcp-doc-limit-item-desc">
                  MCP server access is available on most AI platforms, but what's allowed can vary
                  by plan. Paid tiers usually offer the full experience, with unlimited tool calls
                  and larger context windows. On lower-tier plans, some assistants restrict MCP
                  functionality or reduce how much data you can retrieve in a single session. If the
                  assistant stops mid-query or says it can't access external tools, your plan's
                  limits are the likely reason.
                </div>
              </div>
              <div className="mcp-doc-limit-item">
                <div className="mcp-doc-limit-item-title">
                  The only write action is changing an alert's status
                </div>
                <div className="mcp-doc-limit-item-desc">
                  With the write permission granted, the assistant can change an alert's status,
                  following the same rules and restrictions as the platform. It cannot change
                  account settings, create or delete alerts, or modify anything else in
                  Chargeback.io. Without the write permission it cannot change anything. Verify any
                  status change in the dashboard.
                </div>
              </div>
              <div className="mcp-doc-limit-item">
                <div className="mcp-doc-limit-item-title">
                  Data reflects the moment of the request
                </div>
                <div className="mcp-doc-limit-item-desc">
                  The assistant fetches live data each time you ask. It has no memory of previous
                  queries between sessions, and it won't notice if something changed since you last
                  asked. Ask again to get a fresh snapshot.
                </div>
              </div>
              <div className="mcp-doc-limit-item">
                <div className="mcp-doc-limit-item-title">
                  Only alerts and business accounts are available
                </div>
                <div className="mcp-doc-limit-item-desc">
                  The MCP exposes alerts and business account data. Invoices, API keys, billing, and
                  account-level configuration are not accessible through the MCP. For those, use the
                  Chargeback.io dashboard or API directly.
                </div>
              </div>
            </div>
          </section>

          {/* ── Security & permissions ────────────────────────────── */}
          <section className="mcp-doc-section" id="security">
            <h2 className="mcp-doc-section-title">Security &amp; permissions</h2>
            <p className="mcp-doc-section-intro">What you're agreeing to when you connect.</p>
            <div className="mcp-doc-prose">
              <p>
                During setup, you choose which data the AI client can access, and whether to grant
                the write permission. We recommend selecting both alerts and business accounts. Read
                access is read-only, so granting it carries no risk. The write permission lets the
                assistant change alert statuses, so grant it only if you want that. If you have
                security concerns, keep the connection read-only.
              </p>
              <p>
                Connections use short-lived access tokens (24 hours) and longer-lived refresh tokens
                (90 days). Revoking a connection invalidates both immediately. The client loses
                access the next time it tries to call Chargeback.io.
              </p>
              <p>
                The MCP server itself holds no copy of your data. Every request is forwarded in real
                time to Chargeback.io's API under your credentials, and only data your account has
                permission to see is returned.
              </p>
              <p>
                Chargeback.io logs all MCP tool calls, including which tool was used and the
                parameters sent with each request. Logs are retained internally.
              </p>
            </div>
          </section>

          {/* ── Security tips ─────────────────────────────────────── */}
          <section className="mcp-doc-section" id="security-tips">
            <h2 className="mcp-doc-section-title">Security tips</h2>
            <p className="mcp-doc-section-intro">
              A few habits that reduce risk when using AI assistants with live business data.
            </p>
            <div className="mcp-doc-tip-list">
              <div className="mcp-doc-tip-item">
                <div className="mcp-doc-tip-title danger">
                  Never paste credentials or sensitive data into the chat
                </div>
                <div className="mcp-doc-tip-desc">
                  Do not type passwords, API keys, card numbers, or any personal or financial data
                  directly into any conversation. AI assistants log conversation history. That data
                  can be reviewed by the AI provider or exposed if your account is compromised. The
                  MCP fetches your Chargeback.io data securely in the background, so you should
                  never need to share credentials manually.
                </div>
              </div>
              <div className="mcp-doc-tip-item">
                <div className="mcp-doc-tip-title">
                  Don't share AI sessions that have Chargeback connected
                </div>
                <div className="mcp-doc-tip-desc">
                  If you share a Claude or ChatGPT conversation, the other person can see any
                  Chargeback.io data the assistant retrieved in that session. Keep MCP-connected
                  sessions private.
                </div>
              </div>
              <div className="mcp-doc-tip-item">
                <div className="mcp-doc-tip-title">Revoke connections you no longer use</div>
                <div className="mcp-doc-tip-desc">
                  If you stop using a particular AI client, remove the Chargeback connection from
                  that client's connector or MCP settings. Refresh tokens stay valid for 90 days, so
                  an idle connection is still live access until explicitly removed.
                </div>
              </div>
              <div className="mcp-doc-tip-item">
                <div className="mcp-doc-tip-title">
                  Be aware of prompt injection when using multiple MCP servers
                </div>
                <div className="mcp-doc-tip-desc">
                  <p>
                    Prompt injection is a technique where malicious text is hidden inside external
                    data, such as content in an alert or account name, to influence the assistant's
                    behavior. If you connect read-only, the damage any injected instruction could
                    cause is limited. Once you grant the write permission, an injected instruction
                    could try to change an alert's status, so the risk is higher.
                  </p>
                  <p>
                    A few habits help. If you have multiple MCP servers connected at once, be
                    cautious about unverified data flowing between them. When the assistant reports
                    something unexpected, check the raw data in the dashboard instead of acting on
                    its interpretation alone. If your AI client offers a "confirm before executing"
                    or human-in-the-loop setting for tool calls, turn it on.
                  </p>
                </div>
              </div>
              <div className="mcp-doc-tip-item">
                <div className="mcp-doc-tip-title">Verify sensitive figures in the dashboard</div>
                <div className="mcp-doc-tip-desc">
                  Before acting on a number the assistant gives you, such as a count, a total, or a
                  date, confirm it in Chargeback.io. AI assistants can misread or miscount data,
                  especially across large result sets.
                </div>
              </div>
              <div className="mcp-doc-tip-item">
                <div className="mcp-doc-tip-title">Only connect AI clients you trust</div>
                <div className="mcp-doc-tip-desc">
                  When you connect Chargeback.io to an AI client, that client gets access to your
                  account data for the duration of the session. Only connect clients from sources
                  you trust. Be cautious with MCP servers or connectors found through third-party
                  directories or marketplaces. If you didn't set up a connection intentionally,
                  revoke it in your AI client's settings.
                </div>
              </div>
            </div>
          </section>

          {/* ── Changelog ────────────────────────────────────────── */}
          <section className="mcp-doc-section" id="changelog">
            <h2 className="mcp-doc-section-title">Changelog</h2>
            <p className="mcp-doc-section-intro">
              All notable changes to the Chargeback MCP are documented here.
            </p>
            <Changelog />
          </section>

          {/* ── CTA ──────────────────────────────────────────────── */}
          <div className="mcp-doc-cta-block">
            <div>
              <div className="mcp-doc-cta-eyebrow">Get started</div>
              <div className="mcp-doc-cta-heading">Need help connecting?</div>
              <p className="mcp-doc-cta-desc">
                Our support team can help you set up the MCP, troubleshoot a connection, or answer
                questions about access and permissions.
              </p>
            </div>
            <a
              href="https://www.chargeback.io/contact"
              target="_blank"
              rel="noreferrer"
              className="mcp-doc-cta-btn"
            >
              <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              Contact support
            </a>
          </div>
        </div>
      </main>
    </div>
  )
}

export default McpDocPage
